import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import LoadingSpinner from '../common/LoadingSpinner';
import toast from 'react-hot-toast';
import { FiMail, FiCheck, FiEye, FiTrash2, FiSend, FiX, FiInbox } from 'react-icons/fi';

const STATUS_STYLES = {
  new: { bg: '#ef444422', color: '#ef4444', label: 'New' },
  read: { bg: '#f59e0b22', color: '#f59e0b', label: 'Read' },
  replied: { bg: '#22c55e22', color: '#22c55e', label: 'Replied' }
};

const TYPE_LABELS = {
  feedback: 'Feedback',
  suggestion: 'Suggestion',
  bug: 'Bug Report',
  support: 'Support',
  other: 'Other'
};

export default function Messages() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyTo, setReplyTo] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      const { data } = await api.get('/contact/all');
      setContacts(data);
    } catch {
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkRead = async (id) => {
    try {
      const { data } = await api.put(`/contact/${id}`, { status: 'read' });
      setContacts(prev => prev.map(c => c._id === id ? data : c));
    } catch {
      toast.error('Failed to update');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await api.delete(`/contact/${id}`);
      setContacts(prev => prev.filter(c => c._id !== id));
      toast.success('Message deleted');
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleReply = async () => {
    if (!replyMessage.trim()) return;
    setSending(true);
    try {
      await api.post(`/contact/${replyTo._id}/reply`, { replyMessage });
      setContacts(prev => prev.map(c => c._id === replyTo._id ? { ...c, status: 'replied' } : c));
      toast.success(`Reply sent to ${replyTo.email}`);
      setReplyTo(null);
      setReplyMessage('');
    } catch {
      toast.error('Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const filtered = filter === 'all' ? contacts : contacts.filter(c => c.status === filter);
  const counts = {
    all: contacts.length,
    new: contacts.filter(c => c.status === 'new').length,
    read: contacts.filter(c => c.status === 'read').length,
    replied: contacts.filter(c => c.status === 'replied').length
  };

  if (loading) return <LoadingSpinner text="Loading messages..." />;

  return (
    <div>
      <h1 style={styles.title}><FiInbox size={28} /> Messages</h1>

      {/* Filter tabs */}
      <div style={styles.filters}>
        {['all', 'new', 'read', 'replied'].map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            style={{ ...styles.filterBtn, ...(filter === f ? styles.filterBtnActive : {}) }}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
            <span style={styles.filterCount}>{counts[f]}</span>
          </button>
        ))}
      </div>

      {/* Messages list */}
      {filtered.length === 0 ? (
        <div style={styles.empty}>
          <FiMail size={48} style={{ color: 'var(--text-muted)', marginBottom: 16 }} />
          <p style={{ color: 'var(--text-muted)' }}>No {filter === 'all' ? '' : filter} messages</p>
        </div>
      ) : (
        <div style={styles.list}>
          {filtered.map(contact => {
            const s = STATUS_STYLES[contact.status] || STATUS_STYLES.new;
            return (
              <div key={contact._id} style={{ ...styles.card, borderLeftColor: s.color }}>
                <div style={styles.cardHeader}>
                  <div style={styles.headerLeft}>
                    <span style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-primary)' }}>{contact.name}</span>
                    <a href={`mailto:${contact.email}`} style={styles.email}>{contact.email}</a>
                  </div>
                  <div style={styles.headerRight}>
                    <span style={{ ...styles.typeBadge }}>{TYPE_LABELS[contact.type] || contact.type}</span>
                    <span style={{ ...styles.statusBadge, background: s.bg, color: s.color }}>{s.label}</span>
                  </div>
                </div>

                <p style={styles.message}>{contact.message}</p>

                <div style={styles.cardFooter}>
                  <span style={styles.date}>{new Date(contact.createdAt).toLocaleString()}</span>
                  <div style={styles.actions}>
                    {contact.status === 'new' && (
                      <button onClick={() => handleMarkRead(contact._id)} style={styles.actionBtn} title="Mark as read">
                        <FiEye size={14} /> Read
                      </button>
                    )}
                    <button onClick={() => { setReplyTo(contact); setReplyMessage(''); }} style={{ ...styles.actionBtn, color: 'var(--accent)' }} title="Reply">
                      <FiSend size={14} /> Reply
                    </button>
                    <button onClick={() => handleDelete(contact._id)} style={{ ...styles.actionBtn, color: '#ef4444' }} title="Delete">
                      <FiTrash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Reply Modal */}
      {replyTo && (
        <div style={styles.modalOverlay} onClick={() => setReplyTo(null)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={{ margin: 0, fontSize: 18, color: 'var(--text-primary)' }}>Reply to {replyTo.name}</h3>
              <button onClick={() => setReplyTo(null)} style={styles.closeBtn}><FiX size={20} /></button>
            </div>

            <div style={styles.modalBody}>
              <div style={styles.originalMessage}>
                <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>ORIGINAL MESSAGE</span>
                <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginTop: 6, lineHeight: 1.6 }}>{replyTo.message}</p>
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>From: {replyTo.email} • {new Date(replyTo.createdAt).toLocaleString()}</span>
              </div>

              <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6, display: 'block' }}>YOUR REPLY</label>
              <textarea
                value={replyMessage}
                onChange={e => setReplyMessage(e.target.value)}
                placeholder="Type your reply here..."
                rows={6}
                style={styles.replyTextarea}
              />
            </div>

            <div style={styles.modalFooter}>
              <button onClick={() => setReplyTo(null)} style={styles.cancelBtn}>Cancel</button>
              <button onClick={handleReply} disabled={!replyMessage.trim() || sending} style={styles.sendBtn}>
                {sending ? 'Sending...' : <><FiSend size={14} /> Send Reply</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  title: { display: 'flex', alignItems: 'center', gap: 10, fontSize: 28, fontWeight: 700, marginBottom: 24, color: 'var(--text-primary)' },
  filters: { display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' },
  filterBtn: {
    display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px',
    background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 8,
    color: 'var(--text-secondary)', fontSize: 13, fontWeight: 500, cursor: 'pointer'
  },
  filterBtnActive: { background: '#6366f122', borderColor: 'var(--accent)', color: 'var(--accent)', fontWeight: 600 },
  filterCount: { background: 'var(--bg-primary)', padding: '1px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700 },
  empty: { textAlign: 'center', padding: 60, background: 'var(--bg-secondary)', borderRadius: 16, border: '1px solid var(--border)' },
  list: { display: 'flex', flexDirection: 'column', gap: 12 },
  card: {
    background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 12,
    padding: 20, borderLeft: '4px solid var(--border)'
  },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12, flexWrap: 'wrap', gap: 8 },
  headerLeft: { display: 'flex', flexDirection: 'column', gap: 2 },
  headerRight: { display: 'flex', gap: 6, alignItems: 'center' },
  email: { fontSize: 13, color: 'var(--accent)', textDecoration: 'none' },
  typeBadge: { padding: '3px 10px', background: 'var(--bg-primary)', borderRadius: 12, fontSize: 11, fontWeight: 600, color: 'var(--text-muted)' },
  statusBadge: { padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700 },
  message: { fontSize: 14, color: 'var(--text-primary)', lineHeight: 1.7, whiteSpace: 'pre-wrap', marginBottom: 12 },
  cardFooter: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 },
  date: { fontSize: 12, color: 'var(--text-muted)' },
  actions: { display: 'flex', gap: 4 },
  actionBtn: {
    display: 'flex', alignItems: 'center', gap: 4, padding: '5px 10px',
    background: 'none', border: '1px solid var(--border)', borderRadius: 6,
    color: 'var(--text-secondary)', fontSize: 12, cursor: 'pointer'
  },
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 },
  modal: { background: 'var(--bg-secondary)', borderRadius: 16, width: '100%', maxWidth: 560, maxHeight: '90vh', overflow: 'auto' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid var(--border)' },
  closeBtn: { background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 4, display: 'flex' },
  modalBody: { padding: 24 },
  originalMessage: { background: 'var(--bg-primary)', borderRadius: 10, padding: 16, marginBottom: 20 },
  replyTextarea: {
    width: '100%', padding: 14, background: 'var(--bg-primary)', border: '1px solid var(--border)',
    borderRadius: 10, color: 'var(--text-primary)', fontSize: 14, lineHeight: 1.6, resize: 'vertical', outline: 'none',
    minHeight: 120
  },
  modalFooter: { display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '16px 24px', borderTop: '1px solid var(--border)' },
  cancelBtn: { padding: '10px 20px', background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-secondary)', fontSize: 14, cursor: 'pointer' },
  sendBtn: {
    display: 'flex', alignItems: 'center', gap: 6, padding: '10px 20px',
    background: 'var(--accent)', border: 'none', borderRadius: 8, color: 'white',
    fontSize: 14, fontWeight: 600, cursor: 'pointer'
  }
};
